import React, { useState, useEffect, useRef } from 'react';
import { TopNav, SideNav } from '../navigation/ConsoleNavigation';
import axios from 'axios';
import UnstructuredThreadDetails from './UnstructuredThreadDetails';
import UnstructuredThreadForm from './UnstructuredThreadForm';
import UnstructuredThreadList from './UnstructuredThreadList';
import ReferenceLibrary from './ReferenceLibrary';
import GetCookie from '../../utils/GetCookie'
import ErrorPopup from '../../utils/popup/ErrorPopup';
import WarningPopup from '../../utils/popup/WarningPopup';
import SuccessPopup from '../../utils/popup/SuccessPopup';
import ProcessingPopup from '../../utils/popup/ProcessingPopup';
import authNavigate from '../../globalComponents/AuthNav';
import { useNavigate, useLocation } from 'react-router-dom';
import { calculateFileHash, generateUUID } from '../../utils/HashUtils';
import { usePermissions } from '../../utils/PermissionsContext';
import { PERMISSIONS } from '../../utils/permissionRoutes';
import { useSelector, useDispatch } from 'react-redux';
import {
  setThreads,
  setTotal,
  setPage,
  setSize,
  setSearch,
  setFilterType,
  setShowForm,
  setShowRefLibrary,
  setFormData,
  setSelectedUsers,
  setThreadDetails,
  setEditingThread,
  loadEditingThread,
  openThreadDetails,
  resetForm,
  clearPendingFolderAction,
  clearProjectOrigin,
} from '../../../store/unstructuredThreadSlice';
import { openFabricEditFromProject } from '../../../store/dataFabricSlice';

const UnstructuredThreadManagement = () => {
  const dispatch = useDispatch();

  // ── Redux-persisted state (survives page navigation) ──────────────────────
  const {
    threads, total, page, size, search, filterType,
    showForm, showRefLibrary, editingThread, threadDetails, formData, selectedUsers,
    fromProject, returnToFabricId, pendingThreadEditId, pendingCreate,
  } = useSelector((state) => state.unstructuredThread);

  // ── Local transient state (resets on mount — intentional) ─────────────────
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const { can, refreshPermissions } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const [fabricId, setFabricId] = useState(() => location.state?.fabricId || null);
  const stateProcessedRef = useRef(false);

  // Files state — non-serializable, never goes in Redux
  const [files, setFiles] = useState({
    pdfFiles: [],
    wordFiles: [],
    pptFiles: [],
    textFiles: [],
    excelFiles: [],
    csvFiles: []
  });

  // Popup states — transient
  const [isProcessingPopupVisible, setIsProcessingPopupVisible] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  const [isErrorPopupVisible, setIsErrorPopupVisible] = useState(false);
  const [popupErrorMessage, setPopupErrorMessage] = useState('');
  const [isWarningPopupVisible, setIsWarningPopupVisible] = useState(false);
  const [popupWarningMessage, setPopupWarningMessage] = useState('');
  const [popupWarningTitle, setPopupWarningTitle] = useState('');
  const [isSuccessPopupVisible, setIsSuccessPopupVisible] = useState(false);
  const [popupSuccessMessage, setPopupSuccessMessage] = useState('');
  // Track which file UUIDs have ref_extract enabled
  const [refExtractFiles, setRefExtractFiles] = useState(new Set());
  const [refLibraryTotal, setRefLibraryTotal] = useState(0);

  const API_BASE_URL = '/pceapi/threads/unstructured';  
  const headers = { Authorization: GetCookie('access_token') };

  // Skipped when arriving from a project — the folder list is not rendered,
  // the user goes straight into the create or edit form.
  // NOTE: fromProject deliberately kept out of the dep array (see above).
  useEffect(() => {
    if (fromProject) return;
    fetchThreads();
  }, [page, size, search, refreshTrigger, filterType, fabricId]);
  
  useEffect(() => {
    if (threadDetails?.thread?.id) {
      fetchThreadDetails(threadDetails.thread.id);
    }
  }, []);

  useEffect(() => {
    if (stateProcessedRef.current) {
      stateProcessedRef.current = false;
      return;
    }

    const fId = location.state?.fabricId;
    const viewThreadId = location.state?.viewThreadId;

    setFabricId(fId || null);

    if (viewThreadId) fetchThreadDetails(viewThreadId);

    if (location.state && Object.keys(location.state).length > 0) {
      stateProcessedRef.current = true;
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state]);

  // Arriving from the project form — open create or edit directly,
  // the user never sees the Data Folders list in this mode.
  useEffect(() => {
    if (pendingCreate) {
      resetLocalForm();
      dispatch(setShowForm(true));
      dispatch(clearPendingFolderAction());
      return;
    }
    if (pendingThreadEditId) {
      dispatch(setShowForm(true));
      openThreadEditById(pendingThreadEditId);
    }
  }, [pendingCreate, pendingThreadEditId]);

  // The threads-tree rows carry only id/name/document_count, so fetch the full
  // record before populating the edit form — otherwise tags and user access
  // would open blank.
  const openThreadEditById = async (threadId) => {
    setIsProcessingPopupVisible(true);
    setProcessingMessage('Loading Data Folder');

    try {
      const response = await axios.get(`${API_BASE_URL}/details/${threadId}`, { headers });
      const data = response.data;

      if (data.status === 'NotAuthorized') {
        authNavigate('/login', 'iam', navigate);
        return;
      }

      if (data.status === 'failed') {
        setPopupErrorMessage(data.message || 'Failed to load Data Folder details');
        setIsErrorPopupVisible(true);
        return;
      }

      if (data.data && data.data.thread) {
        handleOpenEditForm(data.data.thread);
      }
    } catch (error) {
      console.error('Error opening Data Folder edit form:', error);
      setPopupErrorMessage(error.message || 'Failed to load Data Folder details');
      setIsErrorPopupVisible(true);
    } finally {
      setIsProcessingPopupVisible(false);
      dispatch(clearPendingFolderAction());
    }
  };

  // Hand the user back to the project edit screen they came from.
  const returnToProject = async () => {
    const fabricId = returnToFabricId;
    const cleanupProjectOrigin = () => {
      dispatch(clearProjectOrigin());
      dispatch(resetForm());
    };

    try {
      const token = GetCookie('access_token');
      const response = await axios.get(`/pceapi/fabrics/details/${fabricId}`, {
        headers: { Authorization: token },
      });
      const data = response.data;
      if (data?.data?.fabric) {
        dispatch(openFabricEditFromProject({ fabricId, fabricData: data.data.fabric }));
        await authNavigate('/dashboard', 'iam', navigate);
        setTimeout(cleanupProjectOrigin, 0);
        return;
      }
    } catch (e) {
      // Fall through to original flow
    }
    dispatch(openFabricEditFromProject({ fabricId }));
    await authNavigate('/dashboard', 'iam', navigate);
    setTimeout(cleanupProjectOrigin, 0);
  };
  
// Clean up ref_extract state when all files are removed
useEffect(() => {
  const allCurrentFiles = Object.values(files).flat();
  
  // If no files but ref_extract has entries, clear it
  if (allCurrentFiles.length === 0 && refExtractFiles.size > 0) {
    setRefExtractFiles(new Set());
  }
}, [files, refExtractFiles]);
  // Fetch threads with pagination and search
  const fetchThreads = async () => {
    setIsProcessingPopupVisible(true);
    setProcessingMessage('Loading data folders');

    try {
      const response = await axios.post(
        `${API_BASE_URL}/list`,
        { page, size, search: search || undefined, filter_type: filterType, fabric_id: fabricId || undefined },
        { headers }
      );

      const data = response.data;

      if (data.status === 'NotAuthorized') {
        setIsProcessingPopupVisible(false);
        authNavigate('/login', 'iam', navigate);
        return;
      }

      if (data.status === 'failed') {
        setIsProcessingPopupVisible(false);
        setPopupErrorMessage(data.message || 'Failed to load data folders');
        setIsErrorPopupVisible(true);
        return;
      }

      dispatch(setThreads(data.data || []));
      dispatch(setTotal(data.total || 0));
    } catch (error) {
      console.error('Error fetching data folder:', error);
      setPopupErrorMessage(error.message || 'Failed to load data folder');
      setIsErrorPopupVisible(true);
    } finally {
      setIsProcessingPopupVisible(false);
    }
  };

  // Fetch thread details for viewing
  const fetchThreadDetails = async (threadId) => {
    setIsProcessingPopupVisible(true);
    setProcessingMessage('Loading...');

    try {
      const response = await axios.get(
        `${API_BASE_URL}/details/${threadId}`,
        { headers }
      );

      const data = response.data;

      if (data.status === 'NotAuthorized') {
        setIsProcessingPopupVisible(false);
        authNavigate('/login', 'iam', navigate);
        return;
      }

      if (data.status === 'failed') {
        if (data.code === 'FOLDER_DELETED') {
          handleFolderDeleted(data.message);
          return;
        }
        setIsProcessingPopupVisible(false);
        setPopupErrorMessage(data.message || 'Failed to load data folder details');
        setIsErrorPopupVisible(true);
        return;
      }

      dispatch(setThreadDetails(data.data));
    } catch (error) {
      console.error('Error fetching Data Folder details:', error);
      setPopupErrorMessage(error.message || 'Failed to load data folder details');
      setIsErrorPopupVisible(true);
    } finally {
      setIsProcessingPopupVisible(false);
    }
  };

  // Reset form + files (local files reset here, Redux form reset via slice)
  const resetLocalForm = () => {
  dispatch(resetForm());
  setFiles({
    pdfFiles: [],
    wordFiles: [],
    pptFiles: [],
    textFiles: [],
    excelFiles: [],
    csvFiles: [],
  });
  setRefExtractFiles(new Set());
};

  // ── BMS-specific: complex S3 upload submit handler ────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setPopupErrorMessage('Data folder name is required');
      setIsErrorPopupVisible(true);
      return;
    }

    setIsProcessingPopupVisible(true);
    setProcessingMessage(editingThread ? 'Processing files and updating data folder' : 'Processing files and creating data folder');

    let createdThreadId = null;
    let isNewThread = !editingThread;

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name.trim());
      formDataToSend.append('thread_access_level', formData.thread_access_level);

      if (formData.tags.length > 0) {
        formDataToSend.append('tags', JSON.stringify(formData.tags));
      }

      if (formData.thread_access_level === 'PRIVATE' && selectedUsers.length > 0) {
        formDataToSend.append('user_access', JSON.stringify(selectedUsers));
      }

      // Process files and create metadata
      const fileMetadata = {};
      const fileUploads = [];

      const fileEntries = Object.entries(files).flatMap(([fileType, fileList]) =>
        fileList.map(fileItem => ({ fileType, fileItem }))
      );
      let hashedFiles = 0;

      // Hashed all at once; results keep the order the files were chosen in
      const hashedEntries = await Promise.all(fileEntries.map(async ({ fileType, fileItem }) => {
        const fileId = fileItem.id; // Use existing UUID from file selection
        const file = fileItem.file; // Extract actual File object
        const hashValue = await calculateFileHash(file);
        hashedFiles += 1;
        setProcessingMessage(`Preparing files (${hashedFiles}/${fileEntries.length})...`);

        const metadata = {
          file_id: fileId,
          file_name: file.name,
          file_size_mb: file.size / (1024 * 1024),
          hash_value: hashValue,
          ref_extract: refExtractFiles.has(fileId) // Check if file is selected for ref extraction
        };
        return { fileType, fileId, file, metadata };
      }));

      for (const { fileType, fileId, file, metadata } of hashedEntries) {
        (fileMetadata[fileType] = fileMetadata[fileType] || []).push(metadata);
        fileUploads.push({ fileId, file, metadata });
      }

      Object.entries(fileMetadata).forEach(([fileType, metadata]) => {
        if (metadata.length > 0) {
          formDataToSend.append(fileType, JSON.stringify(metadata));
        }
      });

      if (editingThread) {
        formDataToSend.append('thread_id', editingThread.id);
        // The tags this form opened with, so the backend changes only what was
        // added or removed here and leaves others' edits since then alone.
        formDataToSend.append('original_tags', JSON.stringify(editingThread.tags || []));
        // Lets the backend say "removed from this project" if the folder was
        // unlinked from it while this user was editing.
        if (fromProject && returnToFabricId) {
          formDataToSend.append('fabric_id', returnToFabricId);
        }
      }

      // Step 1: Create/Update thread and get presigned URLs
      let response;
      try {
        if (editingThread) {
          response = await axios.post(
            `${API_BASE_URL}/update`,
            formDataToSend,
            { headers: { ...headers, 'Content-Type': 'multipart/form-data' } }
          );
        } else {
          response = await axios.post(
            `${API_BASE_URL}/create`,
            formDataToSend,
            { headers: { ...headers, 'Content-Type': 'multipart/form-data' } }
          );
        }
      } catch (createError) {
        setIsProcessingPopupVisible(false);
        setPopupErrorMessage(
          createError.response?.data?.message ||
          createError.message ||
          (editingThread ? 'Failed to update data folder' : 'Failed to create data folder')
        );
        setIsErrorPopupVisible(true);
        return;
      }

      const result = response.data;

      if (result.status === 'NotAuthorized') {
        setIsProcessingPopupVisible(false);
        authNavigate('/login', 'iam', navigate);
        return;
      }

      if (result.status === 'failed') {
        if (result.code === 'FOLDER_DELETED') {
          handleFolderDeleted(result.message);
          return;
        }
        if (result.code === 'FOLDER_REMOVED_FROM_PROJECT') {
          handleFolderRemovedFromProject(result.message);
          return;
        }
        setIsProcessingPopupVisible(false);
        setPopupErrorMessage(result.message || (editingThread ? 'Failed to update data folder' : 'Failed to create data folder'));
        setIsErrorPopupVisible(true);
        return;
      }

      createdThreadId = result.thread_id;

      // Saved tags this user's role couldn't remove; the backend kept them.
      const keptTagsNote = (result.kept_tags || []).length > 0
        ? ` Your role can add meta tags but not remove saved ones, so these were kept: ${result.kept_tags.join(', ')}.`
        : '';
      // The save above has gone through even when the form stays open for a
      // retry: reload the record so tags saved just now lock like any saved
      // tag, and the form shows the tags the folder really has.
      const refreshEditingRecord = async () => {
        if (!editingThread) return;
        try {
          const detailsResponse = await axios.get(`${API_BASE_URL}/details/${editingThread.id}`, { headers });
          const freshThread = detailsResponse.data?.data?.thread;
          if (freshThread) {
            dispatch(setEditingThread(freshThread));
            handleSetFormData(prev => ({ ...prev, tags: freshThread.tags || [] }));
          }
        } catch (refreshError) {
          console.error('Could not reload the data folder after saving:', refreshError);
        }
      };

      // Step 2: Upload files to S3 using presigned URLs
      const presignedUrls = result.presigned_urls || {};
      const skippedFiles = Object.values(result.skipped_files || {});
      const uploadedFileIds = [];
      const failedUploads = [];

      if (Object.keys(presignedUrls).length > 0) {
        setProcessingMessage('Uploading files to cloud storage...');

        const uploadsToSend = fileUploads.filter(({ fileId }) => presignedUrls[fileId]?.presigned_url);
        let finishedUploads = 0;

        await Promise.all(uploadsToSend.map(async ({ fileId, file }) => {
          const urlInfo = presignedUrls[fileId];
          try {
            await axios.put(urlInfo.presigned_url, file, {
              headers: { 'Content-Type': file.type }
            });
            uploadedFileIds.push(urlInfo.document_id);
          } catch (uploadError) {
            console.error(`Failed to upload file ${file.name}:`, uploadError);
            failedUploads.push(file.name);
          }
          finishedUploads += 1;
          setProcessingMessage(`Uploading files to cloud storage (${finishedUploads}/${uploadsToSend.length})...`);
        }));

        // Step 3: Notify backend of completed uploads
        setProcessingMessage('Finalizing upload...');

        const failedFileIds = Object.values(presignedUrls)
          .map(u => u.document_id)
          .filter(id => !uploadedFileIds.includes(id));

        try {
          const completeResponse = await axios.post(
            `${API_BASE_URL}/complete-upload`,
            {
              thread_id: createdThreadId,
              uploaded_files: uploadedFileIds,
              failed_files: failedFileIds,
              fabric_id: fromProject && returnToFabricId ? returnToFabricId : undefined
            },
            { headers }
          );

          const completeResult = completeResponse.data;

          if (completeResult.status === 'failed') {
            // Deleted by someone else while the files were uploading; the
            // backend has already discarded what reached S3.
            if (completeResult.code === 'FOLDER_DELETED') {
              handleFolderDeleted(completeResult.message);
              return;
            }
            if (completeResult.code === 'FOLDER_REMOVED_FROM_PROJECT') {
              handleFolderRemovedFromProject(completeResult.message);
              return;
            }
            if (isNewThread && uploadedFileIds.length === 0) {
              await cleanupFailedThread(createdThreadId);
              setIsProcessingPopupVisible(false);
              setPopupErrorMessage('Data folder creation failed. All changes have been rolled back.');
              setIsErrorPopupVisible(true);
              return;
            }
            setIsProcessingPopupVisible(false);
            setPopupErrorMessage((completeResult.message || 'Some files failed to upload') + keptTagsNote);
            setIsErrorPopupVisible(true);
            await refreshEditingRecord();
            return;
          }
        } catch (completeError) {
          console.error('Complete upload error:', completeError);

          if (isNewThread && uploadedFileIds.length === 0) {
            await cleanupFailedThread(createdThreadId);
            setIsProcessingPopupVisible(false);
            setPopupErrorMessage('Data Folder creation failed. All changes have been rolled back.');
            setIsErrorPopupVisible(true);
            return;
          }

          setIsProcessingPopupVisible(false);
          if (uploadedFileIds.length > 0) {
            setPopupErrorMessage(`Data Folder ${editingThread ? 'updated' : 'created'} with ${uploadedFileIds.length} file(s), but finalization failed: ${completeError.message}`);
          } else {
            setPopupErrorMessage('Failed to finalize upload');
          }
          setIsErrorPopupVisible(true);
          return;
        }
      }

      // Report what actually happened. result.message only says what the backend
      // was willing to accept before any byte reached S3, so a file whose PUT
      // failed still counts there.
      setIsProcessingPopupVisible(false);

      const names = (list) => list.map(f => f.file_name).join(', ');
      const nothingLanded = uploadedFileIds.length === 0 && skippedFiles.length > 0;

      if (failedUploads.length > 0) {
        const attempted = uploadedFileIds.length + failedUploads.length;
        setPopupErrorMessage(
          `${uploadedFileIds.length} of ${attempted} files uploaded. `
          + `Failed: ${failedUploads.join(', ')}. Please try uploading these again.`
          + keptTagsNote
        );
        setIsErrorPopupVisible(true);
      } else if (nothingLanded) {
        const inFolder = skippedFiles.filter(f => f.code !== 'DUPLICATE_IN_UPLOAD');
        const inUpload = skippedFiles.filter(f => f.code === 'DUPLICATE_IN_UPLOAD');
        let msg = 'No documents were added.';
        if (inFolder.length > 0) {
          msg += ` ${inFolder.length} file(s) already exist in this Data Folder: ${names(inFolder)}.`;
        }
        if (inUpload.length > 0) {
          msg += ` ${inUpload.length} file(s) were duplicates within this upload: ${names(inUpload)}.`;
        }
        setPopupErrorMessage(msg + keptTagsNote);
        setIsErrorPopupVisible(true);
      } else {
        let msg = result.message || (editingThread ? 'Data folder updated successfully' : 'Data folder created successfully');
        if (skippedFiles.length > 0) {
          msg += ` ${skippedFiles.length} duplicate file(s) skipped: ${names(skippedFiles)}.`;
        }
        setPopupSuccessMessage(msg);
        setIsSuccessPopupVisible(true);
      }

      if (failedUploads.length > 0 || nothingLanded) {
        await refreshEditingRecord();
      }

      setRefreshTrigger(prev => prev + 1);

      // Keep the form and the user's selection when the message asks for a retry.
      if (failedUploads.length === 0 && !nothingLanded) {
        // Came from a project — go back there once the success popup has been seen
        if (fromProject) {
          setTimeout(() => returnToProject(), 1200);
        } else {
          resetLocalForm();
        }
      }
    } catch (error) {
      console.error('Submit error:', error);

      if (isNewThread && createdThreadId) {
        await cleanupFailedThread(createdThreadId);
        setPopupErrorMessage('Data folder creation failed. All changes have been rolled back.');
      } else {
        setPopupErrorMessage(error.message || (editingThread ? 'Failed to update data folder' : 'Failed to create data folder'));
      }

      setIsProcessingPopupVisible(false);
      setIsErrorPopupVisible(true);
    }
  };

  // Helper function to cleanup failed thread creation
  const cleanupFailedThread = async (threadId) => {
    try {
      await axios.delete(
        `${API_BASE_URL}/delete-thread`,
        { headers, data: { thread_id: threadId } }
      );
      console.log(`Cleaned up failed Data Folder: ${threadId}`);
    } catch (cleanupError) {
      console.error('Failed to cleanup Data Folder:', cleanupError);
    }
  };

  // Open form for editing a thread
  // loadEditingThread reducer handles: editingThread set, showForm=true,
  // threadDetails=null, formData populate, selectedUsers populate
  const handleOpenEditForm = (thread) => {
    dispatch(loadEditingThread(thread));
    setFiles({ pdfFiles: [], wordFiles: [], pptFiles: [], textFiles: [], excelFiles: [], csvFiles: [] });
  };

  // Open thread details view
  const handleOpenThreadDetails = (threadId) => {
    dispatch(openThreadDetails());
    fetchThreadDetails(threadId);
  };

  // Close thread details view
  const handleCloseThreadDetails = () => {
    dispatch(setThreadDetails(null));
    // Refetch — the folder may have been changed/deleted by another user meanwhile
    setRefreshTrigger(prev => prev + 1);
  };

  // The folder this user was editing, uploading to or had open went away
  // under them — deleted, or unlinked from the project that gave them access.
  // Warn rather than error, and drop everything that still shows it (details
  // page, upload form, "Uploading" rows). Opened from a project: go back
  // there once the warning is closed, as a successful save does.
  const returnToProjectAfterWarning = useRef(false);
  const handleFolderGone = (message, title) => {
    setIsProcessingPopupVisible(false);
    setPopupWarningTitle(title);
    setPopupWarningMessage(message);
    setIsWarningPopupVisible(true);
    dispatch(setThreadDetails(null));
    if (fromProject) {
      returnToProjectAfterWarning.current = true;
    } else {
      resetLocalForm();
    }
    setRefreshTrigger(prev => prev + 1);
  };
  const handleFolderDeleted = (message) => handleFolderGone(
    message || 'This data folder has been deleted by another user.',
    'Data folder deleted'
  );
  const handleFolderRemovedFromProject = (message) => handleFolderGone(
    message || 'This data folder was removed from this project.',
    'Data folder removed from project'
  );

  // Popup close handlers
  const closeErrorPopup = () => setIsErrorPopupVisible(false);
  const closeWarningPopup = () => {
    setIsWarningPopupVisible(false);
    if (returnToProjectAfterWarning.current) {
      returnToProjectAfterWarning.current = false;
      returnToProject();
    }
  };
  const closeSuccessPopup = () => setIsSuccessPopupVisible(false);

  const handleRefreshThreadDetails = () => {
    if (threadDetails && threadDetails.thread) {
      fetchThreadDetails(threadDetails.thread.id);
    }
  };

  // ── Wrapper setters — child components may use functional updater pattern ──
  const handleSetFormData = (updater) => {
    if (typeof updater === 'function') {
      dispatch(setFormData(updater(formData)));
    } else {
      dispatch(setFormData(updater));
    }
  };

  const handleSetSelectedUsers = (updater) => {
    if (typeof updater === 'function') {
      dispatch(setSelectedUsers(updater(selectedUsers)));
    } else {
      dispatch(setSelectedUsers(updater));
    }
  };

  const handleSetPage = (val) => dispatch(setPage(val));
  const handleSetSize = (val) => dispatch(setSize(val));
  const handleSetSearch = (val) => dispatch(setSearch(val));
  const handleSetFilterType = (val) => dispatch(setFilterType(val));
  const isProjectFolderFlow = fromProject || pendingCreate || pendingThreadEditId;

  return (
    <div className="main-window">
      <TopNav />
      <div className="container-fluid">
        <div className="row">
          <SideNav />
          <main className="col-12 col-md-9 col-lg-10 ms-sm-auto console-main-window">
            <div className="mx-5 pb-5">
              <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center mb-0 margin-top-4">
                <p className="ev-med-p-blue mb-1">
                  {isProjectFolderFlow ? (
                    <>Data Folder</>
                  ) : showRefLibrary ? (
                     <>Reference Library <span className="ev-light-reg ev-bold-med">({refLibraryTotal})</span></>
                  ) : (
                    <>Data Folders {!showForm && !threadDetails && <span className="ev-light-reg">({total})</span>}</>
                  )}
                </p>
                {fromProject ? (
                  <button
                    className="ev-btn-blue-outline-round ev-btn-med ev-bold-reg"
                    onClick={returnToProject}
                  >
                    <i className="bi bi-arrow-left me-1"></i> Back to Project
                  </button>
                ) : showRefLibrary ? (
                  <button
                    className="ev-btn-blue-outline-round ev-btn-med ev-bold-reg"
                    onClick={() => dispatch(setShowRefLibrary(false))}
                  >
                    {/* Opened from a folder's document list, Back returns to that folder */}
                    {threadDetails ? 'Back to Data Folder' : 'Back to Data folders'}
                  </button>
                  ) : !showForm && !threadDetails && (
                  <div className="d-flex gap-2">
                    <button
                      className="ev-btn-blue-outline-round ev-btn-med ev-bold-reg"
                      onClick={() => dispatch(setShowRefLibrary(true))}
                    >
                      <i className="bi bi-book me-1"></i> Reference Library
                    </button>
                    {can(PERMISSIONS.THREAD_CREATE) && (
                      <button
                        className="ev-btn-yellow-fill-round ev-btn-med ev-bold-reg"
                        onClick={() => {
                          resetLocalForm();
                          dispatch(setShowForm(true));
                        }}
                      >
                        <i className="bi bi-plus-circle me-1"></i> Create Data Folder
                      </button>
                    )}
                  </div>
                )}
              </div>
              {!isProjectFolderFlow && (
                <p className="ev-message-card2 mb-4">
                  {showRefLibrary
                    ? 'Browse, edit, and deduplicate references extracted from your documents.'
                    : 'Manage Data Folders and document processing.'}
                </p>
              )}


              {/* Show form, thread details, or thread list based on state */}
              {showRefLibrary ? (
                <ReferenceLibrary
                  onClose={() => dispatch(setShowRefLibrary(false))}
                  onCountChange={setRefLibraryTotal}
                />
              ) : showForm ? (
                <UnstructuredThreadForm
                  editingThread={editingThread}
                  formData={formData}
                  setFormData={handleSetFormData}
                  selectedUsers={selectedUsers}
                  setSelectedUsers={handleSetSelectedUsers}
                  files={files}
                  setFiles={setFiles}
                  refExtractFiles={refExtractFiles}
                  setRefExtractFiles={setRefExtractFiles}
                  onSubmit={handleSubmit}
                  fromProject={fromProject}
                  onCancel={() => {
                    if (fromProject) {
                      returnToProject();
                      return;
                    }
                    dispatch(setShowForm(false));
                    dispatch(resetForm());
                    setFiles({ pdfFiles: [], wordFiles: [], pptFiles: [], textFiles: [], excelFiles: [], csvFiles: [] });
                    setRefExtractFiles(new Set());
                    // Refetch — the folder may have been changed/deleted by another user meanwhile
                    setRefreshTrigger(prev => prev + 1);
                  }}
                />
              ) : threadDetails ? (
                <UnstructuredThreadDetails
                  threadDetails={threadDetails}
                  onClose={handleCloseThreadDetails}
                  onRefresh={handleRefreshThreadDetails}
                  onFolderDeleted={handleFolderDeleted}
                />
              ) : fromProject ? null : (
                <UnstructuredThreadList
                  threads={threads}
                  total={total}
                  page={page}
                  setPage={handleSetPage}
                  size={size}
                  setSize={handleSetSize}
                  search={search}
                  setSearch={handleSetSearch}
                  filterType={filterType}
                  setFilterType={handleSetFilterType}
                  onRefresh={() => {
                    setRefreshTrigger(prev => prev + 1);
                    refreshPermissions();
                  }}
                  onEditThread={handleOpenEditForm}
                  onViewThreadDetails={handleOpenThreadDetails}
                />
              )}
            </div>
          </main>
        </div>
      </div>

      {/* Popups */}
      {isProcessingPopupVisible && <ProcessingPopup message={processingMessage} />}
      {isErrorPopupVisible && <ErrorPopup onClose={closeErrorPopup} message={popupErrorMessage} />}
      {isWarningPopupVisible && (
        <WarningPopup title={popupWarningTitle} onClose={closeWarningPopup} message={popupWarningMessage} />
      )}
      {isSuccessPopupVisible && <SuccessPopup onClose={closeSuccessPopup} message={popupSuccessMessage} />}
    </div>
  );
};

export default UnstructuredThreadManagement;
